// Import browser-image-compression for advanced compression
import imageCompression from 'browser-image-compression';

// Import heic2any for HEIC conversion
let heic2any;
try {
  heic2any = require('heic2any');
} catch (e) {
  console.warn('heic2any not available, HEIC conversion disabled');
}

/**
 * Validates if a file is an image with acceptable size and format
 * @param {File} file - The file to validate
 * @param {number} maxSizeMB - Maximum allowed size in MB
 * @returns {Object} - { isValid: boolean, message: string | null }
 */
export const validateImage = (file, maxSizeMB = 5) => {
  // Check if file exists
  if (!file) {
    return { isValid: false, message: 'No file selected' };
  }
  
  // Check if it's an image
  if (!file.type.startsWith('image/')) {
    return { isValid: false, message: 'Selected file is not an image' };
  }
  
  // Check allowed formats (jpeg, png, heic)
  const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/heic', 'image/heif'];
  if (!allowedTypes.includes(file.type)) {
    return { isValid: false, message: 'Image must be JPEG, PNG or HEIC format' };
  }
  
  // Check file size
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return { isValid: false, message: `Image must be smaller than ${maxSizeMB}MB` };
  }
  
  return { isValid: true, message: null };
};

/**
 * Creates an object URL for an image file
 * @param {File} file - The image file
 * @returns {string|null} - Object URL or null if invalid
 */
export const createImagePreview = (file) => {
  if (!file || !file.type.startsWith('image/')) {
    return null;
  }
  return URL.createObjectURL(file);
};

/**
 * Revokes an object URL to free up memory
 * @param {string} objectUrl - The object URL to revoke
 */
export const revokeImagePreview = (objectUrl) => {
  if (objectUrl) {
    URL.revokeObjectURL(objectUrl);
  }
};

/**
 * Compresses an image file before upload
 * @param {File} file - The image file to compress
 * @param {Object} options - Compression options
 * @returns {Promise<File>} - Compressed file
 */
export const compressImage = async (file, options = {}) => {
  const { maxWidth = 1600, maxHeight = 1600, quality = 0.8 } = options;
  
  return new Promise((resolve) => {
    // If not an image, return original file
    if (!file || !file.type.startsWith('image/')) {
      resolve(file);
      return;
    }
    
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      
      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let width = img.width;
        let height = img.height;
        
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
        
        // Create canvas and draw resized image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to blob
        canvas.toBlob((blob) => {
          if (!blob) {
            // If compression fails, return original
            resolve(file);
            return;
          }
          
          // Create new file from blob
          const compressedFile = new File([blob], file.name, {
            type: 'image/jpeg',
            lastModified: Date.now(),
          });
          
          resolve(compressedFile);
        }, 'image/jpeg', quality);
      };
    };
  });
};

/**
 * Compresses an image file using browser-image-compression library
 * This provides better compression before crop operation
 * @param {File} file - The image file to compress
 * @param {Object} options - Compression options
 * @returns {Promise<File>} - Compressed file
 */
export const compressImageAdvanced = async (file, options = {}) => {
  try {
    // If not an image, return original file
    if (!file || !file.type.startsWith('image/')) {
      console.log('Not an image file, skipping compression');
      return file;
    }

    console.log('Starting advanced compression with browser-image-compression...', {
      fileName: file.name,
      originalSize: `${(file.size / 1024 / 1024).toFixed(2)}MB`,
      type: file.type
    });

    // Configure compression options
    const compressionOptions = {
      maxSizeMB: options.maxSizeMB || 1, // Target max size in MB (default 1MB)
      maxWidthOrHeight: options.maxWidthOrHeight || 1920, // Max width or height
      useWebWorker: true, // Use web worker for better performance
      fileType: 'image/jpeg', // Convert to JPEG for better compression
      initialQuality: options.quality || 0.8, // Initial quality (0-1)
      ...options
    };

    // Compress the image
    const compressedFile = await imageCompression(file, compressionOptions);

    console.log('Advanced compression complete:', {
      fileName: compressedFile.name,
      originalSize: `${(file.size / 1024 / 1024).toFixed(2)}MB`,
      compressedSize: `${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`,
      reduction: `${(((file.size - compressedFile.size) / file.size) * 100).toFixed(1)}%`
    });

    return compressedFile;
  } catch (error) {
    console.error('Error in advanced compression:', error);
    console.warn('Falling back to original file due to compression error');
    return file; // Return original file if compression fails
  }
};

/**
 * Converts HEIC file to JPEG
 * @param {File} file - The HEIC file to convert
 * @returns {Promise<File>} - Converted JPEG file
 */
export const convertHeicToJpeg = async (file) => {
  // Check if file is HEIC
  const isHeic = file.type === 'image/heic' || 
                 file.type === 'image/heif' || 
                 file.name.toLowerCase().endsWith('.heic') ||
                 file.name.toLowerCase().endsWith('.heif');
  
  if (!isHeic) {
    return file; // Return original file if not HEIC
  }
  
  if (!heic2any) {
    console.warn('heic2any not available, returning original file');
    return file;
  }
  
  try {
    console.log('Converting HEIC to JPEG...');
    
    const maxSizeBytes = 4 * 1024 * 1024; // 4MB
    let quality = 0.8; // Start with 80% quality for HEIC
    let convertedFile;
    
    // Try different quality levels until we get under 4MB
    while (quality > 0.3) {
      // Convert HEIC to JPEG blob
      const blob = await heic2any({
        blob: file,
        toType: 'image/jpeg',
        quality: quality
      });
      
      // Create new file from blob
      const fileName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
      convertedFile = new File([blob], fileName, {
        type: 'image/jpeg',
        lastModified: Date.now()
      });
      
      console.log(`HEIC conversion at quality ${quality}: ${(convertedFile.size / 1024 / 1024).toFixed(2)}MB`);
      
      // If file is under 4MB, we're done
      if (convertedFile.size <= maxSizeBytes) {
        break;
      }
      
      // Reduce quality for next attempt
      quality -= 0.1;
    }
    
    if (convertedFile.size > maxSizeBytes) {
      throw new Error('Unable to convert HEIC to a small enough file. Please use a smaller image.');
    }
    
    console.log(`HEIC conversion successful: ${convertedFile.name} (${(convertedFile.size / 1024 / 1024).toFixed(2)}MB)`);
    return convertedFile;
  } catch (error) {
    console.error('Error converting HEIC to JPEG:', error);
    
    // If the error is that the image is already browser readable, handle it
    if (error && error.code === 1 && error.message && error.message.includes('already browser readable')) {
      console.log('Image is already in a browser-readable format');
      
      const maxSizeBytes = 4 * 1024 * 1024; // 4MB
      
      // Check if the file is over 4MB
      if (file.size > maxSizeBytes) {
        console.log(`File is ${(file.size / 1024 / 1024).toFixed(2)}MB, need to compress`);
        
        // Use the existing compressImage function to reduce size
        const compressedFile = await compressImage(file, maxSizeBytes);
        
        // If it's a PNG that was auto-converted from HEIC, rename to .jpg
        if (file.name.toLowerCase().endsWith('.heic')) {
          const fileName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
          return new File([compressedFile], fileName, {
            type: compressedFile.type,
            lastModified: Date.now()
          });
        }
        
        return compressedFile;
      }
      
      // If it's under 4MB and a PNG that was auto-converted from HEIC, just rename it
      if (file.type === 'image/png' && file.name.toLowerCase().endsWith('.heic')) {
        const fileName = file.name.replace(/\.(heic|heif)$/i, '.png');
        return new File([file], fileName, {
          type: file.type,
          lastModified: Date.now()
        });
      }
      
      return file;
    }
    
    throw new Error('Failed to convert HEIC image. Please try another format.');
  }
}; 